import { Toaster as Sonner, type ToasterProps, toast } from 'sonner';

/**
 * Toast notification provider built on Sonner.
 *
 * Place once at the top of the application:
 * ```tsx
 * <Toaster position="top-right" />
 * ```
 *
 * Then call `toast()` from anywhere:
 * ```tsx
 * toast.success('Batch released');
 * toast.error('Failed to acknowledge alarm');
 * toast.warning('OPC-UA connection unstable');
 * ```
 *
 * Accessibility — Sonner emits each toast inside a region with
 * `role="status" aria-live="polite"` by default, which is the right choice
 * for the success/info path. Error/warning toasts surface a higher-priority
 * announcement because Sonner promotes the toast container to
 * `role="alert"` automatically when the toast type is `error`. We pass
 * `containerAriaLabel` so AT users hear a meaningful landmark name.
 */
const Toaster = ({ containerAriaLabel, ...props }: ToasterProps) => {
  return (
    <Sonner
      className="toaster group"
      containerAriaLabel={containerAriaLabel ?? 'Notifications'}
      toastOptions={{
        classNames: {
          toast:
            'glass group toast rounded-2xl group-[.toaster]:text-foreground group-[.toaster]:shadow-[var(--shadow-overlay)]',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
